import { WORK_ARRANGEMENT_OPTIONS, type MatrixFilter } from "@/types";

function newFilter(type: MatrixFilter["type"]): MatrixFilter {
  const id = `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  switch (type) {
    case "age":
      return { id, type: "age", label: "Age", min: undefined, max: undefined };
    case "gender":
      return { id, type: "gender", label: "Gender", operator: "in", values: [] };
    case "nationality":
      return { id, type: "nationality", label: "Nationality", operator: "in", values: [] };
    case "location":
      return { id, type: "location", label: "Location", operator: "in", values: [] };
    case "work_arrangement":
      return { id, type: "work_arrangement", label: "Work Arrangement", operator: "in", values: [] };
    case "custom":
      return { id, type: "custom", label: "Custom", field_description: "", operator: "contains", values: [] };
  }
}

export function FilterEditor({
  filters,
  onChange,
}: {
  filters: MatrixFilter[];
  onChange: (filters: MatrixFilter[]) => void;
}) {
  const update = (i: number, patch: Partial<MatrixFilter>) => {
    onChange(filters.map((f, j) => (j === i ? ({ ...f, ...patch } as MatrixFilter) : f)));
  };
  const remove = (i: number) => onChange(filters.filter((_, j) => j !== i));
  const add = (type: MatrixFilter["type"]) => onChange([...filters, newFilter(type)]);

  return (
    <div className="space-y-3">
      <div className="rounded-badge bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
        <strong>Hard filters</strong> are demographic/qualifier requirements that automatically{" "}
        <strong>reject</strong> candidates who don't meet them. They do <strong>NOT</strong> affect the
        relevance score. Use these for legal age, location restrictions, nationality requirements, etc.
      </div>

      {filters.length === 0 && (
        <p className="text-xs text-text-secondary">No filters configured. Add one below.</p>
      )}

      {filters.map((f, i) => (
        <FilterRow
          key={f.id}
          filter={f}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
        />
      ))}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => add("age")}
          className="rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-near-black hover:text-white"
        >
          + Age
        </button>
        <button
          type="button"
          onClick={() => add("gender")}
          className="rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-near-black hover:text-white"
        >
          + Gender
        </button>
        <button
          type="button"
          onClick={() => add("nationality")}
          className="rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-near-black hover:text-white"
        >
          + Nationality
        </button>
        <button
          type="button"
          onClick={() => add("location")}
          className="rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-near-black hover:text-white"
        >
          + Location
        </button>
        <button
          type="button"
          onClick={() => add("work_arrangement")}
          className="rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-near-black hover:text-white"
        >
          + Work Arrangement
        </button>
        <button
          type="button"
          onClick={() => add("custom")}
          className="rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-near-black hover:text-white"
        >
          + Custom
        </button>
      </div>
    </div>
  );
}

function FilterRow({
  filter,
  onChange,
  onRemove,
}: {
  filter: MatrixFilter;
  onChange: (patch: Partial<MatrixFilter>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-btn border border-border-card bg-surface-secondary p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className="rounded-badge bg-near-black px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              {filter.type}
            </span>
            <input
              className="flex-1 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
              value={filter.label}
              onChange={(e) => onChange({ label: e.target.value } as any)}
              placeholder="Filter label"
            />
          </div>

          {filter.type === "age" && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-text-secondary">Min</span>
              <input
                type="number"
                min={0}
                max={120}
                className="w-20 rounded-btn border border-border-card bg-white px-2 py-1 text-near-black focus:outline-none focus:shadow-focus"
                value={filter.min ?? ""}
                onChange={(e) => onChange({ min: e.target.value ? Number(e.target.value) : undefined } as any)}
              />
              <span className="text-text-secondary">Max</span>
              <input
                type="number"
                min={0}
                max={120}
                className="w-20 rounded-btn border border-border-card bg-white px-2 py-1 text-near-black focus:outline-none focus:shadow-focus"
                value={filter.max ?? ""}
                onChange={(e) => onChange({ max: e.target.value ? Number(e.target.value) : undefined } as any)}
              />
              <span className="text-text-secondary">years (leave blank for no limit)</span>
            </div>
          )}

          {(filter.type === "gender" || filter.type === "nationality" || filter.type === "location") && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs">
                <select
                  className="rounded-btn border border-border-card bg-white px-2 py-1 text-near-black focus:outline-none focus:shadow-focus"
                  value={filter.operator}
                  onChange={(e) => onChange({ operator: e.target.value as "in" | "not_in" } as any)}
                >
                  <option value="in">Must be one of</option>
                  <option value="not_in">Must NOT be one of</option>
                </select>
                <input
                  className="flex-1 rounded-btn border border-border-card bg-white px-2 py-1 text-near-black focus:outline-none focus:shadow-focus"
                  placeholder={
                    filter.type === "gender" ? "Male, Female, Non-binary"
                    : filter.type === "nationality" ? "Filipino, American, …"
                    : "Manila, Cebu, NCR, Philippines"
                  }
                  value={filter.values.join(", ")}
                  onChange={(e) => onChange({ values: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } as any)}
                />
              </div>
              <p className="text-[10px] text-text-secondary">Comma-separated. Matching is case-insensitive substring.</p>
            </div>
          )}

          {filter.type === "work_arrangement" && (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <select
                  className="rounded-btn border border-border-card bg-white px-2 py-1 text-near-black focus:outline-none focus:shadow-focus"
                  value={filter.operator}
                  onChange={(e) => onChange({ operator: e.target.value as "in" | "not_in" } as any)}
                >
                  <option value="in">Must be one of</option>
                  <option value="not_in">Must NOT be one of</option>
                </select>
                <div className="flex flex-wrap gap-1.5">
                  {WORK_ARRANGEMENT_OPTIONS.map((opt) => {
                    const checked = filter.values.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          const next = checked
                            ? filter.values.filter((v) => v !== opt)
                            : [...filter.values, opt];
                          onChange({ values: next } as any);
                        }}
                        className={[
                          "rounded-btn px-3 py-1 text-xs font-medium transition-colors",
                          checked
                            ? "bg-near-black text-white"
                            : "border border-border-card bg-white text-text-focused hover:bg-surface-secondary",
                        ].join(" ")}
                      >
                        {opt === "WFH" ? "WFH (Work From Home)" : opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-[10px] text-text-secondary">
                AI extracts the candidate's preferred setup from their resume; recognizes "remote", "home-based", "in-office", "hybrid", etc.
              </p>
            </div>
          )}

          {filter.type === "custom" && (
            <div className="space-y-1.5">
              <input
                className="w-full rounded-btn border border-border-card bg-white px-2 py-1 text-xs text-near-black focus:outline-none focus:shadow-focus"
                placeholder="What should the AI extract from the resume? e.g. 'Driver's license category'"
                value={filter.field_description}
                onChange={(e) => onChange({ field_description: e.target.value } as any)}
              />
              <div className="flex items-center gap-2 text-xs">
                <select
                  className="rounded-btn border border-border-card bg-white px-2 py-1 text-near-black focus:outline-none focus:shadow-focus"
                  value={filter.operator}
                  onChange={(e) => onChange({ operator: e.target.value as any } as any)}
                >
                  <option value="contains">Contains</option>
                  <option value="not_contains">Does NOT contain</option>
                  <option value="equals">Equals</option>
                  <option value="not_equals">Does NOT equal</option>
                </select>
                <input
                  className="flex-1 rounded-btn border border-border-card bg-white px-2 py-1 text-near-black focus:outline-none focus:shadow-focus"
                  placeholder="Value(s), comma-separated"
                  value={filter.values.join(", ")}
                  onChange={(e) => onChange({ values: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } as any)}
                />
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded-full px-2 py-1 text-sm text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text"
          title="Remove filter"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
